// Phase 0094-P1 — Payroll Run page.
//
// Workflow:
//   1. Pick plant + year + month → Ensure period
//   2. Create run (REGULAR by default)
//   3. Calculate → live grid of employees with status
//   4. Approve → Lock → Generate payslips
//   5. Create payment batch → Mark paid
//   6. Generate finance posting staging

import { useState } from 'react'
import {
  Alert, App, Button, Card, Col, InputNumber, Popconfirm, Row, Select,
  Skeleton, Space, Table, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, DollarOutlined, FileTextOutlined,
  LockOutlined, PlayCircleOutlined, SwapOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { payrollApi, type PayrollRun, type PayrollRunEmployee } from '@/features/payroll/payrollApi'
import apiClient from '@/shared/api/client'
import { usePermission } from '@/shared/hooks/usePermission'

const { Text, Title } = Typography

function statusColour(s: string) {
  if (s === 'CALCULATED') return 'blue'
  if (s === 'APPROVED') return 'success'
  if (s === 'LOCKED') return 'gold'
  if (s === 'PAID') return 'green'
  if (s === 'ATTENDANCE_MISSING' || s === 'SALARY_STRUCTURE_MISSING' || s === 'REVIEW_REQUIRED') return 'warning'
  if (s === 'BLOCKED') return 'error'
  return 'default'
}

interface Plant { id: string; plant_name: string; plant_code?: string }

export default function PayrollRunPage() {
  // AUTH-001 — approve/lock are now permission-gated server-side too
  const canApprove = usePermission('payroll.approve')
  const canLock = usePermission('payroll.lock')
  const { message } = App.useApp()
  const qc = useQueryClient()

  const [plantId, setPlantId] = useState<string | undefined>()
  const [year, setYear] = useState<number>(new Date().getFullYear())
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1)
  const [runId, setRunId] = useState<string | undefined>()

  const { data: plants } = useQuery({
    queryKey: ['plants-for-payroll'],
    queryFn: async () => (await apiClient.get<Plant[]>('/plants')).data,
  })

  const { data: run } = useQuery({
    queryKey: ['payroll-run', runId],
    queryFn: () => payrollApi.getRun(runId!),
    enabled: !!runId,
  })
  const { data: rows, isLoading: rowsLoading } = useQuery({
    queryKey: ['payroll-run-employees', runId],
    queryFn: () => payrollApi.listRunEmployees(runId!),
    enabled: !!runId,
  })

  const createRunMut = useMutation({
    mutationFn: async () => {
      if (!plantId) throw new Error('Pick a plant')
      const period = await payrollApi.ensurePeriod({ plant_id: plantId, year, month })
      const newRun = await payrollApi.createRun({
        period_id: period.id, plant_id: plantId, run_type: 'REGULAR',
      })
      return newRun
    },
    onSuccess: (r) => {
      setRunId(r.id)
      message.success(`Run created for ${month}/${year}`)
    },
    onError: (e: Error) => message.error(e.message),
  })

  const calcMut = useMutation({
    mutationFn: () => payrollApi.calculateRun(runId!),
    onSuccess: (r) => {
      message.success(`Calculated ${r.total_employees} employees, net ₹${r.total_net_payable.toLocaleString()}`)
      qc.invalidateQueries({ queryKey: ['payroll-run', runId] })
      qc.invalidateQueries({ queryKey: ['payroll-run-employees', runId] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const approveMut = useMutation({
    mutationFn: () => payrollApi.approveRun(runId!),
    onSuccess: () => {
      message.success('Run approved')
      qc.invalidateQueries({ queryKey: ['payroll-run', runId] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const lockMut = useMutation({
    mutationFn: () => payrollApi.lockRun(runId!),
    onSuccess: () => {
      message.success('Run locked')
      qc.invalidateQueries({ queryKey: ['payroll-run', runId] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  const payslipsMut = useMutation({
    mutationFn: () => payrollApi.generatePayslips(runId!),
    onSuccess: (r) => message.success(`${r.payslips_generated} payslips generated`),
    onError: (e: Error) => message.error(e.message),
  })

  const batchMut = useMutation({
    mutationFn: () => payrollApi.createPaymentBatch(runId!),
    onSuccess: (b) => message.success(`Batch ${b.batch_no} — ${b.total_employees} employees, ₹${b.total_amount.toLocaleString()}`),
    onError: (e: Error) => message.error(e.message),
  })

  const financeMut = useMutation({
    mutationFn: () => payrollApi.generateFinancePosting(runId!),
    onSuccess: (r) => message.success(`${r.staging_rows_created} finance posting rows staged`),
    onError: (e: Error) => message.error(e.message),
  })

  const columns = [
    { title: 'Employee', dataIndex: 'employee_id', key: 'employee_id',
      render: (id: string) => <code style={{ fontSize: 11 }}>{id.slice(0, 8)}</code> },
    { title: 'Salary Type', dataIndex: 'salary_type', key: 'salary_type',
      render: (t: string | null) => t ? <Tag>{t}</Tag> : '—' },
    { title: 'Payable Days', dataIndex: 'payable_days', key: 'payable_days' },
    { title: 'Present', dataIndex: 'present_days', key: 'present_days' },
    { title: 'LWP', dataIndex: 'lwp_days', key: 'lwp_days',
      render: (n: number) => n > 0 ? <Tag color="warning">{n}</Tag> : '—' },
    { title: 'OT Hours', dataIndex: 'ot_hours', key: 'ot_hours' },
    { title: 'Gross', dataIndex: 'gross_earnings', key: 'gross_earnings',
      render: (n: number) => n > 0 ? `₹${n.toLocaleString()}` : '—' },
    { title: 'Deductions', dataIndex: 'total_deductions', key: 'total_deductions',
      render: (n: number) => n > 0 ? `₹${n.toLocaleString()}` : '—' },
    { title: 'Net Payable', dataIndex: 'net_payable', key: 'net_payable',
      render: (n: number) => <Text strong>₹{n.toLocaleString()}</Text> },
    { title: 'Status', dataIndex: 'calculation_status', key: 'calculation_status',
      render: (s: string) => <Tag color={statusColour(s)}>{s}</Tag> },
    { title: 'Issue', dataIndex: 'error_message', key: 'error_message',
      render: (s: string | null) => s ? <Text type="warning">{s}</Text> : '' },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1500, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Payroll Run</Title>
      <Text type="secondary">
        Pick a period → calculate → review → approve → lock → pay → finance posting.
      </Text>

      <Card style={{ marginTop: 16 }} title="1. Period & Run">
        <Space wrap>
          <Select
            placeholder="Plant"
            style={{ minWidth: 220 }}
            value={plantId}
            onChange={setPlantId}
            options={(plants ?? []).map((p) => ({
              value: p.id, label: p.plant_name,
            }))}
          />
          <InputNumber value={year} onChange={(v) => setYear(Number(v))} min={2020} max={2100} />
          <InputNumber value={month} onChange={(v) => setMonth(Number(v))} min={1} max={12} />
          <Button type="primary" icon={<PlayCircleOutlined />}
                  onClick={() => createRunMut.mutate()}
                  loading={createRunMut.isPending}
                  disabled={!plantId}>
            Open / create run
          </Button>
        </Space>
      </Card>

      {runId && run && (
        <Card style={{ marginTop: 16 }}
              title={
                <Space>
                  <span>Run {run.id.slice(0, 8)}</span>
                  <Tag color={statusColour(run.run_status)}>{run.run_status}</Tag>
                  {run.locked_at && <LockOutlined />}
                </Space>
              }
              extra={
                <Space wrap>
                  {run.run_status === 'DRAFT' || run.run_status === 'CALCULATED' ? (
                    <Button icon={<ThunderboltOutlined />}
                            onClick={() => calcMut.mutate()}
                            loading={calcMut.isPending}>
                      Calculate
                    </Button>
                  ) : null}
                  {run.run_status === 'CALCULATED' && canApprove && (
                    <Button icon={<CheckCircleOutlined />} type="primary"
                            onClick={() => approveMut.mutate()}
                            loading={approveMut.isPending}>
                      Approve
                    </Button>
                  )}
                  {run.run_status === 'APPROVED' && canLock && (
                    <Popconfirm title="Lock this run?"
                                description="Locked runs cannot be recalculated."
                                onConfirm={() => lockMut.mutate()}>
                      <Button icon={<LockOutlined />} loading={lockMut.isPending}>
                        Lock
                      </Button>
                    </Popconfirm>
                  )}
                  <Button icon={<FileTextOutlined />}
                          disabled={!['CALCULATED', 'APPROVED', 'LOCKED', 'PAID'].includes(run.run_status)}
                          onClick={() => payslipsMut.mutate()}
                          loading={payslipsMut.isPending}>
                    Generate Payslips
                  </Button>
                  <Button icon={<DollarOutlined />}
                          disabled={!['APPROVED', 'LOCKED', 'PAID'].includes(run.run_status)}
                          onClick={() => batchMut.mutate()}
                          loading={batchMut.isPending}>
                    Create Payment Batch
                  </Button>
                  <Button icon={<SwapOutlined />}
                          disabled={!['CALCULATED', 'APPROVED', 'LOCKED', 'PAID'].includes(run.run_status)}
                          onClick={() => financeMut.mutate()}
                          loading={financeMut.isPending}>
                    Stage Finance Posting
                  </Button>
                </Space>
              }>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col span={6}><Text type="secondary">Employees</Text>
              <div style={{ fontSize: 24, fontWeight: 700 }}>{run.total_employees}</div></Col>
            <Col span={6}><Text type="secondary">Gross</Text>
              <div style={{ fontSize: 24, fontWeight: 700 }}>₹{run.total_earnings.toLocaleString()}</div></Col>
            <Col span={6}><Text type="secondary">Deductions</Text>
              <div style={{ fontSize: 24, fontWeight: 700 }}>₹{run.total_deductions.toLocaleString()}</div></Col>
            <Col span={6}><Text type="secondary">Net Payable</Text>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1677ff' }}>
                ₹{run.total_net_payable.toLocaleString()}</div></Col>
          </Row>

          {rowsLoading ? <Skeleton active /> : (
            <Table
              rowKey="id"
              columns={columns as never}
              dataSource={rows ?? []}
              pagination={{ pageSize: 50 }}
              size="small"
              scroll={{ x: 1400 }}
            />
          )}

          {(rows ?? []).some(r => r.review_required) && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Some rows need review"
              description="Rows flagged as ATTENDANCE_MISSING, SALARY_STRUCTURE_MISSING, or REVIEW_REQUIRED. Resolve before approving."
            />
          )}
        </Card>
      )}
    </div>
  )
}
